import React, { useEffect, useState } from 'react';
import {
  Dropdown,
  SalvButton,
  SalvCheckbox,
  SalvCheckboxGroup,
  SalvFieldHeader,
  SalvGroupedCheckbox,
  SalvTextArea,
  SalvTooltipWrapper,
  SidePanel,
} from '@salvapp/ui-components';
import { faCircleInfo, faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { FormikState } from 'formik/dist/types';
import { ApiBridgeAlert, ApiSanctionAlert, mapValues } from '../api';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  getBooleanFieldValueDisplayName,
  getDefaultValue,
  getDependentFields,
  getField,
  getMessengerFieldConfiguration,
  getProvidedFieldValue,
  getRequestedFieldToggledByDefaultValue,
  getSchemaType,
  isNonEmptyValue,
  setRequired,
  testInput,
} from './helper';
import {
  attachmentApi,
  bridgeOrganisationApi,
  bridgeSettingsApi,
  bridgeTemplateApi,
  conversationApi,
  conversationCategoryApi,
  monitoringAlertInternalApi,
} from '../apis';
import {
  BridgeApiConversationCategory,
  BridgeApiFriendOrganisationV2,
  BridgeApiTemplate,
  BridgeApiTemplateField,
  BridgeApiTemplateFieldTypeEnum,
  InternalApiAlertType,
  InternalApiBridgeRecipientMappingConf,
  InternalApiManualAlert,
  InternalApiMonitoringAlert,
} from '../api-internal';
import { JsonMessage, JsonMessageTypeEnum } from './Message';
import { useDispatch } from 'react-redux';
import { updateScreeningAlertSuccess } from '../screening/screeningReducer';
import { updateAlertSuccess } from '../alerts/alertReducer';
import CustomRequestedFieldsFormRow from './CustomRequestedFieldsFormRow';
import * as Yup from 'yup';
import './NewRequestSidePanel.scss';
import StructuredMessage from './StructuredMessage';
import StructuredMessageForm from './StructuredMessageForm';
import { sorted } from './common/helper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import { useMe } from '../organisation/useMe';
import { parseErrorMessage } from '../common/errors/helpers';

export interface NewRequestSidePanelProps {
  alert: ApiSanctionAlert | InternalApiMonitoringAlert | ApiBridgeAlert | InternalApiManualAlert;
  show: boolean;
  onComplete: (updated: boolean) => void;
  alertType: InternalApiAlertType;
}

export interface RFISendingFormValues {
  to: DropdownOption | undefined;
  category: DropdownOption | undefined;
  template: DropdownOption | undefined;
  message: string;
  providedFields: {
    [key: string]: string;
  };
  requestedFields: { [key: string]: boolean };
  custom: {
    [key: string]: { type: DropdownOption; displayName: string };
  };
  urgent: boolean;
  isReScreeningEnabled: boolean;
}

const convertForSelect = (name: string, id: string): DropdownOption => {
  return { label: name, key: id, value: id };
};

function constructValidationSchema(template: BridgeApiTemplate | undefined) {
  if (!template) {
    return undefined;
  }

  return Yup.object().shape(
    {
      to: Yup.object().shape({
        key: Yup.string().required('Required'),
        label: Yup.string().required('Required'),
        value: Yup.string().required('Required'),
      }),
      message: template.customMessage
        ? Yup.string()
            .required()
            .test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value))
        : Yup.string(),
      category: Yup.object().shape({
        key: Yup.string(),
        label: Yup.string().required('Required'),
        value: Yup.string().required('Required'),
      }),
      template: Yup.object().shape({
        key: Yup.string(),
        label: Yup.string().required('Required'),
        value: Yup.string().required('Required'),
      }),
      providedFields: Yup.object().shape(
        template.providedFields.reduce((obj, templateField) => {
          return Object.assign(obj, {
            [templateField.name]: setRequired(getSchemaType(templateField), templateField),
          });
        }, {})
      ),
      custom: Yup.lazy((obj) =>
        Yup.object(
          mapValues(obj, () => {
            return Yup.object().shape({
              displayName: Yup.string()
                .required('Required')
                .test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value)),
              type: Yup.object().shape({
                key: Yup.string().required('Required'),
                label: Yup.string().required('Required'),
                value: Yup.string().required('Required'),
              }),
            });
          })
        )
      ),
      urgent: Yup.boolean().required(),
    },
    []
  );
}

async function constructProvidedFields(
  selectedTemplate: BridgeApiTemplate,
  values: RFISendingFormValues,
  recipientOrgId: string
): Promise<{ providedFields: any; attachmentIds: string[] }> {
  const attachmentIds: string[] = [];
  const providedFields = await selectedTemplate.providedFields.reduce(async (acc, templateField) => {
    const providedValue = values.providedFields[templateField.name];
    let constructedValue;
    if (templateField.type === BridgeApiTemplateFieldTypeEnum.FILE) {
      constructedValue = await getFileFieldValue(providedValue, recipientOrgId);
      if (constructedValue) {
        attachmentIds.push(...constructedValue.map((fileVal) => fileVal.id));
      }
    } else {
      constructedValue = providedValue;
    }

    if (constructedValue !== undefined) {
      return {
        ...(await acc),
        [templateField.name]: constructedValue,
      };
    } else {
      return await acc;
    }
  }, Promise.resolve({}));
  return { providedFields: providedFields, attachmentIds: attachmentIds };
}

async function getFileFieldValue(
  providedValue: any,
  recipientOrgId: string
): Promise<{ fileName: string; id: string }[] | undefined> {
  if (!providedValue) {
    return undefined;
  }

  return await Promise.all(
    Object.values(providedValue)
      .filter((fileValue: any) => fileValue instanceof File)
      // @ts-ignore
      .map(async (attachment: File) => {
        const attachmentId = (
          await attachmentApi.uploadMessageAttachment({
            file: attachment,
            counterpartiesIds: [recipientOrgId],
          })
        ).handle;
        return {
          fileName: attachment.name,
          id: attachmentId,
        };
      })
  );
}

export const NewRequestSidePanel = ({ alert, show, onComplete, alertType }: NewRequestSidePanelProps) => {
  const dispatch = useDispatch();

  const [loadingFriends, setLoadingFriends] = useState<boolean>(true);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);
  const [friends, setFriends] = useState<BridgeApiFriendOrganisationV2[]>([]);
  const [categories, setCategories] = useState<BridgeApiConversationCategory[]>([]);
  const [templates, setTemplates] = useState<BridgeApiTemplate[]>([]);
  const [selectedFriend, setSelectedFriend] = useState<DropdownOption | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<DropdownOption | undefined>();
  const [selectedTemplate, setSelectedTemplate] = useState<BridgeApiTemplate | undefined>();
  const { features } = useMe();

  const initialValues: RFISendingFormValues = {
    to: selectedFriend,
    category: selectedCategory ? selectedCategory : undefined,
    template: selectedTemplate
      ? {
          key: selectedTemplate.handle,
          value: selectedTemplate.handle,
          label: selectedTemplate.name,
        }
      : undefined,

    requestedFields: selectedTemplate
      ? selectedTemplate.requestedFields
          .filter((field) => getMessengerFieldConfiguration(field).show_when === undefined)
          .reduce((acc, key) => ({ ...acc, [key.name]: getRequestedFieldToggledByDefaultValue(key) }), {})
      : {},

    providedFields: selectedTemplate
      ? selectedTemplate.providedFields.reduce((obj, templateField) => {
          const providedFieldValue = getProvidedFieldValue(alert, templateField);
          return {
            ...obj,
            [templateField.name]: isNonEmptyValue(providedFieldValue)
              ? providedFieldValue
              : getDefaultValue(templateField),
          };
        }, {})
      : {},
    message: selectedTemplate?.messageValue || '',
    urgent: false,
    custom: {},
    isReScreeningEnabled: true,
  };

  const initialFormikState: Partial<FormikState<RFISendingFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingFriends(true);
        const friends = await bridgeOrganisationApi.getAllFriendOrganisations();
        const configurations = await bridgeSettingsApi.getBridgeRecipientMappingConfs();
        handleRecipient(configurations, friends.content);
        setFriends(friends.content);
      } finally {
        setLoadingFriends(false);
      }
    };
    show && fetchData();
  }, [show]);

  const getPropValue = (
    object: InternalApiMonitoringAlert | ApiBridgeAlert | ApiSanctionAlert | InternalApiManualAlert,
    path = ''
  ): any =>
    // @ts-ignore
    path.split('.').reduce((o, x) => (o == undefined ? o : o[x]), object);

  const handleRecipient = (
    configurations: InternalApiBridgeRecipientMappingConf[],
    orgFriends: BridgeApiFriendOrganisationV2[]
  ) => {
    configurations?.some((conf) => {
      // Loop over fieldIds which indicated where the entity value can be
      return conf.fieldIds.some((fieldId) => {
        // Get the property value
        const value = getPropValue(alert, fieldId);
        if (value) {
          // If property value exists then go over fieldMappings
          return conf.fieldMappings.some((fieldMapping) => {
            if (String(value).trim() === fieldMapping.fieldValue.trim()) {
              // If property value equals fieldMapping.fieldValue then
              // look for a friend organisation which name equals fieldMapping.selectedValue
              const friendExists = orgFriends.find((friend) => friend.name === fieldMapping.selectedValue);
              if (friendExists) {
                // On first match we will set the selected recipient
                // and cancel the function
                setSelectedFriend(convertForSelect(friendExists.name, friendExists.id));
                return true;
              }
            }
          });
        }
      });
    });
  };

  function constructRequestedFields(template: BridgeApiTemplate, values: RFISendingFormValues) {
    const templateFields = Object.keys(values.requestedFields).filter((key) => {
      return values.requestedFields[key];
    });

    const maxTemplateOrder = Math.max(...template.requestedFields.map((r) => r.sortOrder));

    const customFields = Object.keys(values.custom).map((key, num) => {
      return {
        name: key,
        displayName: values.custom[key].displayName,
        type: BridgeApiTemplateFieldTypeEnum[
          values.custom[key].type.value as keyof typeof BridgeApiTemplateFieldTypeEnum
        ],
        required: false,
        sortOrder: maxTemplateOrder + num,
      };
    });

    if (!template.chat && templateFields.length === 0 && customFields.length === 0) {
      // If chat is disabled, something needs to be requested
      throw new Error('No requested attributes');
    }

    return {
      fromTemplate: templateFields,
      custom: customFields,
    };
  }

  const submit = async (
    values: RFISendingFormValues,
    { setStatus, setSubmitting, resetForm }: FormikHelpers<RFISendingFormValues>
  ) => {
    try {
      if (!values.to || !values.to.key) {
        setStatus('No recipient selected');
        return;
      }

      const category = values.category;

      if (!values.template || !category || !selectedTemplate) {
        setStatus('No template selected');
        return;
      }

      const recipientOrgId = values.to.key;

      const conversationAttributes = selectedTemplate.providedFields.reduce((obj, templateField) => {
        const value: unknown = values.providedFields[templateField.name];

        return Object.assign(obj, {
          [templateField.name]: value instanceof File ? value.name : value,
        });
      }, {});

      const { providedFields, attachmentIds } = await constructProvidedFields(selectedTemplate, values, recipientOrgId);

      const structuredMessageJson: JsonMessage = {
        type: JsonMessageTypeEnum.STRUCTURED_REQUEST,
        message: values.message,
        attributes: providedFields,
      };

      await conversationApi.createConversation({
        bridgeApiCreateConversationV2: {
          alertId: alert.id,
          alertType,
          title:
            category.label === values.template.label ? category.label : category.label + ' - ' + values.template.label,
          categoryId: category.key,
          templateId: values.template.key,
          recipientOrganisationId: recipientOrgId,
          urgent: values.urgent,
          messageContent: JSON.stringify(structuredMessageJson),
          attributes: JSON.stringify(conversationAttributes),
          requestedFields: constructRequestedFields(selectedTemplate, values),
          messageAttachmentIds: attachmentIds,
          isReScreeningEnabled: values.isReScreeningEnabled,
        },
      });

      if (alertType === InternalApiAlertType.SCREENING) {
        await dispatch(updateScreeningAlertSuccess());
      } else if (alertType === InternalApiAlertType.MONITORING) {
        const updatedAlert = await monitoringAlertInternalApi.getAlertInternal({
          alertId: alert.id,
        });
        await dispatch(updateAlertSuccess(updatedAlert));
      }

      resetForm(initialFormikState);
      onComplete(true);
    } catch (e) {
      setStatus(await parseErrorMessage(e));
    }
    setSubmitting(false);
  };

  const parentRequestedFields = selectedTemplate
    ? sorted(
        selectedTemplate.requestedFields.filter(
          (field) => getMessengerFieldConfiguration(field).show_when === undefined
        )
      )
    : [];

  const dependentRequestedFields = selectedTemplate ? getDependentFields(selectedTemplate) : {};

  function dependentFieldInfo(templateField: BridgeApiTemplateField, dependentFields: BridgeApiTemplateField[]) {
    if (!dependentFields || dependentFields.length === 0) {
      return;
    }

    const options =
      templateField.type === BridgeApiTemplateFieldTypeEnum.BOOLEAN
        ? [true, false]
        : templateField.type === BridgeApiTemplateFieldTypeEnum.SELECT
          ? getMessengerFieldConfiguration(templateField).select_options || []
          : [];

    return (
      <>
        {options.map((value) => {
          const dependentFieldForValue = dependentFields.filter(
            (field) => getMessengerFieldConfiguration(field).show_when?.field_value === value
          );
          if (dependentFieldForValue.length === 0) {
            return;
          }

          const requiredAnswer =
            typeof value === 'boolean' ? getBooleanFieldValueDisplayName(value, templateField) : value;

          return (
            <div key={`${templateField.id}_dependencies_${value}`}>
              {`If recipient answers "${requiredAnswer}" request has additional fields:`}
              {dependentFieldForValue.map((dependentField) => (
                <div key={`dependent_${dependentField.name}`}>- {dependentField.displayName}</div>
              ))}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <div className='new-request-side-panel'>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        initialTouched={{
          to: true,
          category: true,
          group: true,
          template: true,
        }}
        onSubmit={submit}
        validationSchema={constructValidationSchema(selectedTemplate)}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ isSubmitting, values, setFieldValue, errors }: FormikProps<RFISendingFormValues>) => {
          useEffect(() => {
            const fetchData = async () => {
              if (!values.to?.value) return;
              try {
                setLoadingCategories(true);
                const categories = await conversationCategoryApi.getPossibleCategoriesForSendingRfi({
                  recipientOrganisationId: values.to?.value,
                });
                setCategories(categories);
              } finally {
                setLoadingCategories(false);
              }
            };
            fetchData();
            setSelectedFriend(values.to);
            setSelectedCategory(undefined);
            setSelectedTemplate(undefined);
          }, [values.to]);

          useEffect(() => {
            const fetchData = async () => {
              if (!values.category?.key) return;
              try {
                setLoadingTemplates(true);
                const templates = await bridgeTemplateApi.getTemplatesByCategoryId({
                  categoryId: values.category.key,
                  recipientOrganisationId: values.to?.value,
                  page: 0,
                  size: 50,
                });
                setTemplates(templates.content);
              } finally {
                setLoadingTemplates(false);
              }
            };
            fetchData();
            setSelectedCategory(values.category);
            setSelectedTemplate(undefined);
          }, [values.category?.key]);

          useEffect(() => {
            if (values.template) {
              const template = templates.find((template) => template.handle === values.template?.key);

              setSelectedTemplate(template);
            }
          }, [values.template?.key]);

          const requestedFieldNames = values
            ? Object.entries(values.requestedFields)
                .filter(([, enabled]) => enabled)
                .map(([name]) => name)
            : [];

          const customFields = Object.keys(values.custom)
            .filter((key) => values.custom[key].displayName && values.custom[key].type)
            .map((fieldId, index) => {
              return {
                name: fieldId,
                displayName: values.custom[fieldId].displayName,
                type: values.custom[fieldId].type.value as BridgeApiTemplateFieldTypeEnum,
                required: false,
                sortOrder: requestedFieldNames.length + index,
              };
            });

          const hasRequestedFields = requestedFieldNames.length > 0 || customFields.length > 0;

          return (
            <Form autoComplete='off'>
              <SidePanel
                title='New request'
                show={show}
                onHide={() => onComplete(false)}
                footer={
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <SalvButton className='m-r-8' label='Close' onClick={() => onComplete(false)} variant='secondary' />
                    <SalvButton
                      icon={faPaperPlane}
                      label='Send'
                      disabled={!selectedTemplate || isSubmitting}
                      type='submit'
                      variant='primary'
                    />
                  </div>
                }
              >
                <div className='p-x-16' style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div>
                    <div className='field-title'>To</div>
                    <div>
                      <Dropdown
                        name='to'
                        label=''
                        value={values.to}
                        options={friends.map((friend) => convertForSelect(friend.name, friend.id))}
                        onChange={(value: DropdownOption[]) => setFieldValue('to', value[0])}
                        loading={loadingFriends}
                      />
                    </div>
                  </div>
                  {values.to && (
                    <div>
                      <div className='field-title'>Category</div>
                      <div>
                        <Dropdown
                          name='category'
                          label=''
                          value={values.category}
                          options={categories.map((category) => convertForSelect(category.name, category.handle!))}
                          onChange={(value: DropdownOption[]) => setFieldValue('category', value[0])}
                          loading={loadingCategories}
                        />
                      </div>
                    </div>
                  )}
                  {values.category && (
                    <div>
                      <div className='field-title'>Template</div>
                      <div>
                        <Dropdown
                          name='template'
                          label=''
                          value={values.template}
                          options={templates.map((template) => convertForSelect(template.name, template.handle))}
                          onChange={(value: DropdownOption[]) => setFieldValue('template', value[0])}
                          loading={loadingTemplates}
                        />
                      </div>
                    </div>
                  )}
                  {values.template && (
                    <>
                      <div className='provided-fields'>
                        <div className='field-title'>Provided fields</div>
                        <div>
                          {templates
                            .find((template) => template.handle === values.template?.key)
                            ?.providedFields.map((field) => {
                              const fullFieldName = `providedFields.${field.name}`;

                              return (
                                <div key={field.name} className='provided-field-row'>
                                  <div className='field-name'>
                                    {field.displayName}
                                    {field.required && <span className='required-asterisk'>*</span>}
                                  </div>
                                  <div className='field-value'>
                                    {getField(
                                      field,
                                      values.providedFields[field.name],
                                      errors.providedFields !== undefined && !!errors.providedFields[field.name],
                                      (value: any) => setFieldValue(fullFieldName, value)
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                      <SalvTextArea
                        label='Message'
                        autoSizing={true}
                        onChange={(value: string) => {
                          setFieldValue(`message`, value);
                        }}
                        value={values.message}
                        isInvalid={!!errors.message}
                        maxRows={10}
                        required={true}
                      />
                    </>
                  )}
                  {selectedTemplate && (
                    <div>
                      {(parentRequestedFields.length > 0 || selectedTemplate.customRequestFields) && (
                        <SalvFieldHeader label='Requested fields' />
                      )}
                      {parentRequestedFields.length > 0 && (
                        <SalvCheckboxGroup
                          className='m-b-16'
                          value={values.requestedFields}
                          ariaLabel='Requested fields'
                          onChange={(checkBoxValues) => {
                            for (const requestedField of selectedTemplate.requestedFields) {
                              setFieldValue(
                                `requestedFields.${requestedField.name}`,
                                checkBoxValues.includes(requestedField.name)
                              );
                            }
                          }}
                        >
                          {parentRequestedFields.map((field) => (
                            <div
                              key={field.name}
                              className='requested-field-checkbox'
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <SalvGroupedCheckbox
                                key={field.id}
                                label={field.displayName}
                                value={field.name}
                                description={dependentFieldInfo(field, dependentRequestedFields[field.name])}
                              />
                              {field.description && (
                                <SalvTooltipWrapper tooltip={field.description} placement='right'>
                                  <FontAwesomeIcon
                                    icon={faCircleInfo}
                                    className='text-muted cursor-pointer'
                                    data-testid='description-icon'
                                  />
                                </SalvTooltipWrapper>
                              )}
                            </div>
                          ))}
                        </SalvCheckboxGroup>
                      )}

                      {selectedTemplate.customRequestFields && (
                        <CustomRequestedFieldsFormRow values={values} setFieldValue={setFieldValue} />
                      )}

                      <SalvCheckbox
                        className={`m-b-16${
                          parentRequestedFields.length > 0 && !selectedTemplate.customRequestFields
                            ? ' p-t-16 urgent-checkbox-separator'
                            : ''
                        }`}
                        selected={values.urgent}
                        label='Mark investigation as urgent'
                        onChange={(urgent) => {
                          setFieldValue('urgent', urgent);
                        }}
                      />

                      <SalvFieldHeader label='Preview' description='This is what the recipient will see' />
                      <div className='preview-container'>
                        <StructuredMessage
                          jsonMessage={{
                            type: JsonMessageTypeEnum.STRUCTURED_REQUEST,
                            message: values.message,
                            attributes: selectedTemplate.providedFields.reduce((obj, templateField) => {
                              const value: any = values.providedFields[templateField.name];
                              return isNonEmptyValue(value)
                                ? {
                                    ...obj,
                                    [templateField.name]: value,
                                  }
                                : obj;
                            }, {}),
                          }}
                          template={selectedTemplate}
                        />
                      </div>
                      {hasRequestedFields && (
                        <StructuredMessageForm
                          // @ts-ignore
                          alertRfiId={undefined}
                          key={selectedTemplate.handle + '_requested_preview'}
                          // @ts-ignore
                          conversation={{
                            requestedFields: {
                              fromTemplate: requestedFieldNames,
                              custom: customFields,
                            },
                          }}
                          template={selectedTemplate}
                          onSubmit={() => {}}
                          isPreview={true}
                        />
                      )}
                      {isFeatureEnabled(ORGANISATION_FEATURE.RFI_SCREENING_CONFIGURATION, features) &&
                        alertType === InternalApiAlertType.SCREENING && (
                          <SalvCheckbox
                            label={'Re-screen automatically after response'}
                            className={'m-t-16'}
                            selected={values.isReScreeningEnabled}
                            onChange={(isChecked) => setFieldValue('isReScreeningEnabled', isChecked)}
                          />
                        )}
                    </div>
                  )}
                </div>
              </SidePanel>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default NewRequestSidePanel;
